import { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ColorScheme, Density, DesignDirection, ThemeContext, ThemeState } from "./types";

const STORAGE_KEY_SCHEME = "clones-ui:color-scheme";
const STORAGE_KEY_DENSITY = "clones-ui:density";
const STORAGE_KEY_HIGH_CONTRAST = "clones-ui:high-contrast";
const STORAGE_KEY_REDUCE_MOTION = "clones-ui:reduce-motion";
const STORAGE_KEY_DESIGN = "clones-ui:design";

const defaults: ThemeState = {
  colorScheme: "system",
  resolvedScheme: "light",
  density: "normal",
  design: "studio",
  highContrast: false,
  reduceMotion: false,
};

export const ThemeCtx = createContext<ThemeContext>({
  ...defaults,
  setColorScheme: () => undefined,
  setDensity: () => undefined,
  setDesign: () => undefined,
  setHighContrast: () => undefined,
  setReduceMotion: () => undefined,
});

function readStorage<T extends string>(key: string, fallback: T, allowed: readonly T[]): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw && (allowed as readonly string[]).includes(raw)) return raw as T;
  } catch {
    /* SSR / private browsing */
  }
  return fallback;
}

function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* SSR / private browsing */
  }
}

function resolveScheme(scheme: ColorScheme): "light" | "dark" {
  if (scheme !== "system") return scheme;
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyToHtml(state: ThemeState): void {
  const html = document.documentElement;
  const resolved = state.resolvedScheme;

  html.classList.toggle("dark", resolved === "dark");

  html.dataset.density = state.density;
  html.dataset.design = state.design;

  if (state.highContrast) {
    html.dataset.highContrast = "";
  } else {
    delete html.dataset.highContrast;
  }

  if (state.reduceMotion) {
    html.dataset.reduceMotion = "";
  } else {
    delete html.dataset.reduceMotion;
  }
}

interface ThemeProviderProps {
  children: React.ReactNode;
  /**
   * Override default values for testing or SSR bootstrapping.
   */
  initialScheme?: ColorScheme;
  initialDensity?: Density;
  initialHighContrast?: boolean;
  initialReduceMotion?: boolean;
  /** Surface default; a saved user preference always takes precedence. */
  defaultDesign?: DesignDirection;
}

export function ThemeProvider({
  children,
  initialScheme,
  initialDensity,
  initialHighContrast,
  initialReduceMotion,
  defaultDesign = "studio",
}: ThemeProviderProps) {
  const motionOverridden = useRef(initialReduceMotion !== undefined ||
    Boolean(readStorage(STORAGE_KEY_REDUCE_MOTION, "", ["true", "false"])));
  const [design, setDesignState] = useState<DesignDirection>(() =>
    readStorage(STORAGE_KEY_DESIGN, defaultDesign, ["studio", "editorial", "calm"]),
  );
  const [colorScheme, setColorSchemeState] = useState<ColorScheme>(() =>
    initialScheme ?? readStorage("clones-ui:color-scheme", "system", ["light", "dark", "system"]),
  );
  const [density, setDensityState] = useState<Density>(() =>
    initialDensity ?? readStorage(STORAGE_KEY_DENSITY, "normal", ["compact", "normal", "spacious"]),
  );
  const [highContrast, setHighContrastState] = useState<boolean>(() => {
    if (initialHighContrast !== undefined) return initialHighContrast;
    return readStorage(STORAGE_KEY_HIGH_CONTRAST, "false", ["true", "false"]) === "true";
  });
  const [reduceMotion, setReduceMotionState] = useState<boolean>(() => {
    if (initialReduceMotion !== undefined) return initialReduceMotion;
    const stored = readStorage(STORAGE_KEY_REDUCE_MOTION, "", ["true", "false"]);
    if (stored) return stored === "true";
    if (typeof window !== "undefined") {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }
    return false;
  });

  const [resolvedScheme, setResolvedScheme] = useState<"light" | "dark">(() =>
    resolveScheme(initialScheme ?? colorScheme),
  );

  // Keep the resolved scheme in sync with the selected scheme. When "system"
  // is selected this seeds the value from the current OS preference; the
  // matchMedia listener below keeps it live afterwards.
  useEffect(() => {
    setResolvedScheme(resolveScheme(colorScheme));
  }, [colorScheme]);

  const state: ThemeState = useMemo(
    () => ({ colorScheme, resolvedScheme, density, design, highContrast, reduceMotion }),
    [colorScheme, resolvedScheme, density, design, highContrast, reduceMotion],
  );

  useEffect(() => {
    applyToHtml(state);
  }, [state]);

  // Re-resolve when OS color scheme changes while "system" is selected. Setting
  // resolvedScheme directly (rather than re-setting colorScheme to the same
  // "system" value, which React bails out of as a no-op render) guarantees the
  // <html class="dark"> toggle actually flips when the OS theme changes.
  useEffect(() => {
    if (colorScheme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) =>
      setResolvedScheme(e.matches ? "dark" : "light");
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [colorScheme]);

  // Track the OS preference until the user chooses an explicit motion setting.
  useEffect(() => {
    if (initialReduceMotion !== undefined) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = (event: MediaQueryListEvent) => {
      if (!motionOverridden.current) {
        setReduceMotionState(event.matches);
      }
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [initialReduceMotion]);

  const setColorScheme = useCallback((scheme: ColorScheme) => {
    writeStorage(STORAGE_KEY_SCHEME, scheme);
    setColorSchemeState(scheme);
  }, []);

  const setDensity = useCallback((d: Density) => {
    writeStorage(STORAGE_KEY_DENSITY, d);
    setDensityState(d);
  }, []);

  const setDesign = useCallback((direction: DesignDirection) => {
    writeStorage(STORAGE_KEY_DESIGN, direction);
    setDesignState(direction);
  }, []);

  const setHighContrast = useCallback((enabled: boolean) => {
    writeStorage(STORAGE_KEY_HIGH_CONTRAST, String(enabled));
    setHighContrastState(enabled);
  }, []);

  const setReduceMotion = useCallback((enabled: boolean) => {
    motionOverridden.current = true;
    writeStorage(STORAGE_KEY_REDUCE_MOTION, String(enabled));
    setReduceMotionState(enabled);
  }, []);

  const ctx = useMemo<ThemeContext>(
    () => ({
      ...state,
      setColorScheme,
      setDensity,
      setDesign,
      setHighContrast,
      setReduceMotion,
    }),
    [state, setColorScheme, setDensity, setDesign, setHighContrast, setReduceMotion],
  );

  return <ThemeCtx.Provider value={ctx}>{children}</ThemeCtx.Provider>;
}
